import React, { FocusEvent } from "react";
import { create } from "zustand";
import { ActionPortalType } from "../types/actionPortal.type";
import { IAlertState } from "../interface/alert-state.interface";
import { initAlertState } from "../data/initialize/alert-states.data";
import { SendMsgPlatforms } from "../types/eend-msg-platforms.type";

const TEST_VEHICLE_NUM = '124785';
const TEST_PERSON_ID = '123456782';

export interface IModalOtpData {
    isOtpModalOpen: boolean;
    otpStep: 'pre-otp-input' | 'otp-input';
    alertState: IAlertState;
    modalTitle: string;
    portalAction: ActionPortalType | '',
    otpForm: IOtpForm;
    checkOtpCodeBtnRef: React.RefObject<HTMLButtonElement | null>;
    otpInputRefs: React.RefObject<HTMLInputElement>[];
    mobileOrEmailForGUI: string;
    sendVia: SendMsgPlatforms;
}

export interface IOtpForm {
    isValid: boolean,
    values: {
        personId: string,
        vehicleNum: string,
    },
    errors: {
        personId?: string,
        vehicleNum?: string,
    },
    touched: {
        personId?: boolean,
        vehicleNum?: boolean,
    },
}

const initOtpForm: IOtpForm = {
    isValid: false,
    values: {
        personId: '',
        vehicleNum: '',
    },
    errors: {},
    touched: {},
}

const initModalOtpData: IModalOtpData = {
    isOtpModalOpen: false,
    otpStep: 'pre-otp-input',
    alertState: { ...initAlertState },
    modalTitle: '',
    portalAction: '',
    otpForm: { ...initOtpForm },
    checkOtpCodeBtnRef: React.createRef<HTMLButtonElement>(),
    otpInputRefs: [
        React.createRef<HTMLInputElement>(),
        React.createRef<HTMLInputElement>(),
        React.createRef<HTMLInputElement>(),
        React.createRef<HTMLInputElement>(),
        React.createRef<HTMLInputElement>(),
        React.createRef<HTMLInputElement>(),
    ],
    mobileOrEmailForGUI: '',
    sendVia: "EMAIL"
};

interface IOtpModalStore {
    modalOtpData: IModalOtpData;
    setModalOtpData: (modalOtpData: IModalOtpData) => void;
    resetModalOtpData: () => void;
    openOtpModal: (portalAction: ActionPortalType) => void;
    closeOtpModal: () => void;
    setFormValue: (field: keyof IOtpForm['values'], value: string) => void;
    setBlurAndError: (field: keyof IOtpForm['values'], errors?: {
        personId?: string;
        vehicleNum?: string;
        otpCode?: string;
    }) => void;
    modalOtpHandleBlur(event: FocusEvent<HTMLInputElement>): void;
    setOtpModalAlertState(alertState: IAlertState): void;
    setModalOtpStep(step: 'pre-otp-input' | 'otp-input', mobileOrEmailForGUI?: string, sendVia?: SendMsgPlatforms): void;
}

export const useOtpModalStore = create<IOtpModalStore>()((set) => ({
    modalOtpData: { ...initModalOtpData },
    setModalOtpData: (modalOtpData: IModalOtpData) => set((state) => ({
        ...state,
        modalOtpData: modalOtpData,
    })),
    resetModalOtpData: () => set((state) => ({
        ...state,
        modalOtpData: {
            ...initModalOtpData,
            isOtpModalOpen: true,
        },
    })),
    openOtpModal: (portalAction: ActionPortalType) => set((state) => ({
        ...state,
        modalOtpData: {
            ...state.modalOtpData,
            isOtpModalOpen: true,
            portalAction: portalAction,
        },
    })),
    closeOtpModal: () => set((state) => ({
        ...state,
        modalOtpData: {
            ...initModalOtpData
        },
    })),
    setFormValue: (field: keyof IOtpForm['values'], value: string) => set((state) => ({
        ...state,
        modalOtpData: {
            ...state.modalOtpData,
            otpForm: {
                ...state.modalOtpData.otpForm,
                values: {
                    ...state.modalOtpData.otpForm.values,
                    [field]: value,
                },
            },
        },
    })),
    setBlurAndError: (field: keyof IOtpForm['values'], errors: {
        personId?: string;
        vehicleNum?: string;
        otpCode?: string;
    }) => set((state) => ({
        ...state,
        modalOtpData: {
            ...state.modalOtpData,
            otpForm: {
                ...state.modalOtpData.otpForm,
                isValid: Object.keys(errors).length === 0,
                touched: {
                    ...state.modalOtpData.otpForm.touched,
                    [field]: true,
                },
                errors: {
                    ...errors,
                },
            },
        },
    })),
    modalOtpHandleBlur: (event: FocusEvent<HTMLInputElement>) => {

        const field = event.target.name as keyof IOtpForm['values'];
        const value = event.target.value;

        const errorMsg = value.trim() === '' ? 'This field is required' : undefined;

        set((state) => ({
            ...state,
            modalOtpData: {
                ...state.modalOtpData,
                otpForm: {
                    ...state.modalOtpData.otpForm,
                    touched: {
                        ...state.modalOtpData.otpForm.touched,
                        [field]: true,
                    },
                    errors: {
                        ...state.modalOtpData.otpForm.errors,
                        [field]: errorMsg,
                    },
                },
            },
        }));
    },
    setOtpModalAlertState: (alertState: IAlertState) => set((state) => ({
        ...state,
        modalOtpData: {
            ...state.modalOtpData,
            alertState: alertState,
        },
    })),
    setModalOtpStep: (step: 'pre-otp-input' | 'otp-input', mobileOrEmailForGUI: string = '', sendVia: SendMsgPlatforms = 'EMAIL') => set((state) => ({
        ...state,
        modalOtpData: {
            ...state.modalOtpData,
            otpStep: step,
            mobileOrEmailForGUI: step === 'otp-input' ? mobileOrEmailForGUI : state.modalOtpData.mobileOrEmailForGUI,
            sendVia: step === 'otp-input' ? sendVia : state.modalOtpData.sendVia,
        },
    })),
    setOtpOperationDetails: (mobileOrEmailForGUI: string, sendVia: SendMsgPlatforms) => set((state) => ({
        ...state,
        modalOtpData: {
            ...state.modalOtpData,
            mobileOrEmailForGUI: mobileOrEmailForGUI,
            sendVia: sendVia,
        },
    })),
}));



export const useSetModalOtpStep = () => {
    return useOtpModalStore((state) => state.setModalOtpStep);
}

export const useSetOtpModalAlertState = () => {
    return useOtpModalStore((state) => state.setOtpModalAlertState);
}

export const useSetBlurAndError = () => {
    return useOtpModalStore((state) => state.setBlurAndError);
}

export const useOtpModalHandleBlur = () => {
    return useOtpModalStore((state) => state.modalOtpHandleBlur);
}

export const useSetFormValue = () => {
    return useOtpModalStore((state) => state.setFormValue);
}

export const useResetModalOtpData = () => {
    return useOtpModalStore((state) => state.resetModalOtpData);
}

export const useOpenOtpModal = () => {
    return useOtpModalStore((state) => state.openOtpModal);
}

export const useCloseOtpModal = () => {
    return useOtpModalStore((state) => state.closeOtpModal);
}

export const useSetModalOtpData = () => {
    return useOtpModalStore((state) => state.setModalOtpData);
}

export const useModalOtpData = () => {
    return useOtpModalStore((state) => state.modalOtpData);
}

export const useGetOtpModalAlertState = () => {
    return useOtpModalStore((state) => state.modalOtpData.alertState);
}

export const useIsOtpModalOpen = () => {
    return useOtpModalStore((state) => state.modalOtpData.isOtpModalOpen);
}

export const useGetOtpModalStep = () => {
    return useOtpModalStore((state) => state.modalOtpData.otpStep);
}

export const useGetOtpModalInputRefs = () => {
    return useOtpModalStore((state) => state.modalOtpData.otpInputRefs);
}

export const useGetOtpModalCheckOtpCodeBtnRef = () => {
    return useOtpModalStore((state) => state.modalOtpData.checkOtpCodeBtnRef);
}

export const useGetOtpOperationMobileOrEmailOtpModal = () => {
    return useOtpModalStore((state) => state.modalOtpData.mobileOrEmailForGUI);
}

export const useGetOtpOperationSendViaOtpModal = () => {
    return useOtpModalStore((state) => state.modalOtpData.sendVia);
}